import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * IconButton, the 44px circular icon control.
 *
 * CSS selector: `mobile/atoms.css` `.ds-iconbtn` (+ `--flat`, `--bare`,
 * `--float`).
 *
 * Doctrine: icon-only controls always carry an accessible name and always keep
 * a 44px hit target. The default `raised` surface is the floating card
 * treatment; shadows are permitted here because this is a floating layer.
 * `flat` is the in-sheet surface, `bare` is fully transparent, and `float`
 * absolutely positions the button over artwork, so the parent must be
 * positioned.
 */
const ICONBTN_SURFACE = {
  raised: null,
  flat: 'ds-iconbtn--flat',
  bare: 'ds-iconbtn--bare',
};

const BACKBTN_SURFACE = {
  raised: null,
  flat: 'ds-backbtn--flat',
  bare: 'ds-backbtn--bare',
};

export const IconButton = React.forwardRef(function IconButton(
  { className, surface = 'raised', float = false, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'ds-iconbtn',
        ICONBTN_SURFACE[surface],
        float && 'ds-iconbtn--float',
        className,
      )}
      {...rest}
    />
  );
});

/**
 * Default back chevron so `<BackButton>` works with no children.
 * 24 grid, Feather style, stroke-width 2.25 because back arrows and chevrons
 * take the heavier end of the stroke range.
 */
function BackArrowGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 18 9 12l6-6"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * BackButton, the canonical floating circular back control.
 *
 * CSS selector: `mobile/atoms.css` `.ds-backbtn` (+ `--flat`, `--bare`,
 * `--float`). The CSS gives `.ds-backbtn` and `.ds-iconbtn` byte-identical
 * rules, but the back button gets its own class name because it is the
 * canonical, named element of the mobile chrome, so this component emits
 * `.ds-backbtn` rather than delegating to `<IconButton>`.
 *
 * Doctrine: mobile has no back bar. The back affordance is a floating circular
 * button, and it always exposes a name (defaults to "Back") because the glyph
 * alone means nothing to assistive tech.
 *
 * The component name is the one deliberate rename in this library: "Backbtn"
 * reads badly, so `.ds-backbtn` becomes `BackButton`.
 */
export const BackButton = React.forwardRef(function BackButton(
  {
    className,
    surface = 'raised',
    float = false,
    label = 'Back',
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        'ds-backbtn',
        BACKBTN_SURFACE[surface],
        float && 'ds-backbtn--float',
        className,
      )}
      {...rest}
    >
      {children || <BackArrowGlyph />}
    </button>
  );
});

export default IconButton;
